import { FC, MouseEvent, useCallback, useMemo } from 'react';
import { openExternalLink } from '../../../../utils/openExternalLink';
import { useFavoritesEnabled } from '../../../../utils/useFavoritesEnabled';
import { BusinessType } from '../../../business/types';
import { IconButton } from '../../../input/IconButton';
import { DataTestId } from '../../../layout/DataTestIdWrapper';
import { PostUI, ReactionState } from '../../types';
import { isPostStatusValidForReaction } from '../../utils/isPostStatusValidForReaction';
import { PostDetailsCardProps, usePostDetailsCardContext } from '../PostDetailsCard';
import {
    BookmarkFilledIcon,
    BookmarkIcon,
    DislikeFilledIcon,
    DislikeIcon,
    DislikeThumbFilledIcon,
    DislikeThumbIcon,
    FavoriteHeartIcon,
    LikeThumbFilledIcon,
    LikeThumbIcon,
    PostTextControlsContainer,
    ShareIcon,
    WebsiteIcon,
} from '../styles';
import { makeMediaParams } from '../utils';
import { isPostStatusValidForSave } from './utils/isPostStatusValidForSave';

export interface PostTextControlsProps {
    post: PostUI;
    index: PostDetailsCardProps['index'];
    onReaction: PostDetailsCardProps['onReaction'];
    onBookmark?: PostDetailsCardProps['onBookmark'];
    onShare?: PostDetailsCardProps['onShare'];
}

export const PostTextControls: FC<PostTextControlsProps> = ({
    post,
    index,
    onReaction,
    onBookmark,
    onShare,
}) => {
    const { activeIndex } = usePostDetailsCardContext();
    const isFavoritesEnabled = useFavoritesEnabled();
    const isLiked = post.reactionState === 'like';
    const canReact = isPostStatusValidForReaction(post.status);

    const handleBookmark = useCallback(
        (event: MouseEvent<HTMLElement>) => {
            onBookmark?.();
            event.stopPropagation();
        },
        [onBookmark],
    );

    const handleReaction = useCallback(
        (reactionState: PostUI['reactionState']) => {
            onReaction({ postNumber: index, reactionState });
        },
        [index, onReaction],
    );

    const handleShare = useCallback(
        () => onShare?.(makeMediaParams(post, activeIndex)),
        [onShare, post, activeIndex],
    );

    const handleWebsiteClick = useCallback(() => {
        if (post.business?.siteUrl) {
            openExternalLink(post.business.siteUrl);
        }
    }, [post.business?.siteUrl]);

    const { likeIcon, dislikeIcon, bookmarkIcon } = useMemo(() => {
        const isDisliked = post.reactionState === 'dislike';
        const isSaved = Boolean(post.saved);

        let like;
        if (isFavoritesEnabled) {
            like = isLiked ? <LikeThumbFilledIcon /> : <LikeThumbIcon />;
        } else {
            like = <FavoriteHeartIcon isActive={isLiked} />;
        }

        let dislike;
        if (isFavoritesEnabled) {
            dislike = isDisliked ? <DislikeThumbFilledIcon /> : <DislikeThumbIcon />;
        } else {
            dislike = isDisliked ? <DislikeFilledIcon /> : <DislikeIcon />;
        }

        let bookmark;
        if (isFavoritesEnabled) {
            bookmark = <FavoriteHeartIcon isActive={isSaved} />;
        } else {
            bookmark = isSaved ? <BookmarkFilledIcon /> : <BookmarkIcon />;
        }

        return { likeIcon: like, dislikeIcon: dislike, bookmarkIcon: bookmark };
    }, [isFavoritesEnabled, isLiked, post.reactionState, post.saved]);

    return (
        <PostTextControlsContainer>
            {canReact ? (
                <IconButton
                    dataTestId={DataTestId.LIKE}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleReaction(ReactionState.LIKE);
                    }}
                >
                    {likeIcon}
                </IconButton>
            ) : null}

            {canReact && post.business ? (
                <IconButton
                    dataTestId={DataTestId.DISLIKE}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleReaction(ReactionState.DISLIKE);
                    }}
                >
                    {dislikeIcon}
                </IconButton>
            ) : null}

            {isPostStatusValidForSave(post.status) && !post.ownersData?.isOwner && (
                <IconButton onClick={handleBookmark} dataTestId={DataTestId.BOOKMARK}>
                    {bookmarkIcon}
                </IconButton>
            )}

            <IconButton onClick={handleShare} dataTestId={DataTestId.SHARE}>
                <ShareIcon />
            </IconButton>

            {post.business?.type === BusinessType.BUSINESS && Boolean(post.business?.siteUrl) && (
                <IconButton onClick={handleWebsiteClick}>
                    <WebsiteIcon />
                </IconButton>
            )}
        </PostTextControlsContainer>
    );
};
